import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import type { AddToCartEvent, Product } from '../../../../shared/models/product.model';
import type { GameStarterGuideContent } from '../../models/game-section.model';
import { GAME_SECTION_COPY, interpolateGameCopy } from '../../constants/game-section.constants';
import { StarterGuideGroupCarouselComponent } from './starter-guide-group-carousel.component';

/**
 * Normaliza para comparar el `intro.heading` del panel contra el título fijo
 * del bloque: trim + minúsculas + fuera signos de interrogación. En los 8
 * juegos actuales el admin repitió literalmente «¿Por dónde empezar?» como
 * heading del intro, así que sin esto el bloque pinta el mismo título dos
 * veces seguidas.
 */
function normalizeHeading(value: string): string {
  return value.trim().toLowerCase().replace(/[¿?]/g, '');
}

@Component({
  selector: 'app-starter-guide',
  imports: [ButtonComponent, RouterLink, StarterGuideGroupCarouselComponent],
  templateUrl: './starter-guide.component.html',
  styleUrl: './starter-guide.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
})
export class StarterGuideComponent {
  content = input.required<GameStarterGuideContent>();
  gameName = input.required<string>();
  /** `null` si la sección no tiene categoría: el CTA de catálogo no se pinta. */
  categorySlug = input.required<string | null>();

  zoomOpen = output<Product>();
  addToCart = output<AddToCartEvent>();
  notifyRequest = output<Product>();
  notifyCancel = output<Product>();
  /** Clic en zona no interactiva de la card → la página navega a la ficha. */
  cardClick = output<Product>();
  /** CTA «Reservar» de un producto en precompra → la página abre el modal. */
  reserveClick = output<Product>();

  protected readonly ctaLabel = computed(() =>
    interpolateGameCopy(GAME_SECTION_COPY.starterGuideCta, { game: this.gameName() }),
  );

  protected readonly title = GAME_SECTION_COPY.starterGuideTitle;

  protected readonly subtitle = computed(() =>
    interpolateGameCopy(GAME_SECTION_COPY.starterGuideSubtitle, { game: this.gameName() }),
  );

  /**
   * `null` cuando el heading del intro es, normalizado, el mismo texto que el
   * título fijo del bloque — así se evita repetirlo. Si el panel llega a
   * poner un heading distinto, se respeta y se pinta.
   */
  protected readonly introHeading = computed<string | null>(() => {
    const heading = this.content().intro?.heading;
    if (!heading) return null;
    return normalizeHeading(heading) === normalizeHeading(this.title) ? null : heading;
  });

  protected onZoomOpen(product: Product): void {
    this.zoomOpen.emit(product);
  }

  protected onAddToCart(event: AddToCartEvent): void {
    this.addToCart.emit(event);
  }

  protected onNotifyRequest(product: Product): void {
    this.notifyRequest.emit(product);
  }

  protected onNotifyCancel(product: Product): void {
    this.notifyCancel.emit(product);
  }

  protected onCardClick(product: Product): void {
    this.cardClick.emit(product);
  }

  protected onReserveClick(product: Product): void {
    this.reserveClick.emit(product);
  }
}
